import { useCallback, useMemo } from 'react';
import { useLazyQuery } from '@apollo/client';
import { coverageHoursOptions as coverageHoursOptionsQuery } from 'graphql-queries';

export class CoverageHoursOptionsQueryError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'CoverageHoursOptionsQueryError';
  }
}

export type CoverageHoursCategoryOption = {
  name: string;
  hasCreatableOptions: boolean;
};

export type CoverageHoursIssueTypeOption = {
  category: string;
  issue: string | null;
  isUsed: boolean;
};

export type CoverageHoursOptionsData = {
  categories: CoverageHoursCategoryOption[];
  issueTypes: CoverageHoursIssueTypeOption[];
};

type WireCoverageHoursOptions = CoverageHoursOptionsData & {
  __typename?: string;
};

type CoverageHoursOptionsResponse = {
  coverageHoursOptions: WireCoverageHoursOptions;
};

export type CoverageHoursOptionsStatus = 'loading' | 'success' | 'error';

export type UseCoverageHoursOptionsResult = {
  categories: CoverageHoursCategoryOption[];
  issueTypes: CoverageHoursIssueTypeOption[];
  status: CoverageHoursOptionsStatus;
  error?: CoverageHoursOptionsQueryError;
  fetchCoverageHoursOptions: () => Promise<CoverageHoursOptionsData>;
};

const EMPTY_OPTIONS: CoverageHoursOptionsData = {
  categories: [],
  issueTypes: [],
};

function toStatus(
  hasError: boolean,
  hasResult: boolean
): CoverageHoursOptionsStatus {
  if (hasError && !hasResult) {
    return 'error';
  }

  return hasResult ? 'success' : 'loading';
}

export function useCoverageHoursOptions(
  channelId: string
): UseCoverageHoursOptionsResult {
  const variables = useMemo(() => ({ channelId }), [channelId]);

  const [
    executeQuery,
    { data, previousData, error: queryError },
  ] = useLazyQuery<CoverageHoursOptionsResponse>(coverageHoursOptionsQuery, {
    fetchPolicy: 'network-only',
  });

  const fetchCoverageHoursOptions = useCallback(async () => {
    let result;

    try {
      result = await executeQuery({ variables });
    } catch (cause) {
      throw new CoverageHoursOptionsQueryError(
        'Could not load coverage hours options',
        { cause }
      );
    }

    if (result.error) {
      throw new CoverageHoursOptionsQueryError(
        'Could not load coverage hours options',
        { cause: result.error }
      );
    }

    return result.data?.coverageHoursOptions ?? EMPTY_OPTIONS;
  }, [executeQuery, variables]);

  // previousData keeps the last-loaded options in place while a refetch (e.g.
  // the post-failure reconciliation) is in flight, mirroring useCoverageHours.
  const result = data?.coverageHoursOptions ?? previousData?.coverageHoursOptions ?? null;

  // 'success' once any response has ever arrived, so a later refetch keeps the
  // options on screen instead of flipping the panel back to a spinner.
  const status = toStatus(Boolean(queryError), result !== null);

  const error = useMemo(() => {
    if (!queryError) {
      return undefined;
    }

    return new CoverageHoursOptionsQueryError(
      'Could not load coverage hours options',
      { cause: queryError }
    );
  }, [queryError]);

  return {
    categories: result?.categories ?? EMPTY_OPTIONS.categories,
    issueTypes: result?.issueTypes ?? EMPTY_OPTIONS.issueTypes,
    status,
    error,
    fetchCoverageHoursOptions,
  };
}
